import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarOff, RefreshCw, AlertCircle, CalendarHeart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { getHolidays } from '../../services/referenceDataService';
import { cn } from '../../utils/cn';

interface HolidayRow {
  date: string;       // 'YYYY-MM-DD'
  name: string;
  dte: number;        // days from today (negative for past)
}

const fmtDate = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const daysFromToday = (iso: string): number => {
  const d = new Date(iso + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
};

const HolidaysSection: React.FC = () => {
  const [filter, setFilter] = useState<'upcoming' | 'all'>('upcoming');
  const [rows, setRows] = useState<HolidayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getHolidays();
      const list: HolidayRow[] = Object.entries(r.holidays || {})
        .map(([date, name]) => ({ date, name, dte: daysFromToday(date) }))
        .sort((a, b) => a.date.localeCompare(b.date));
      setRows(list);
    } catch (e: any) {
      setError(e?.message || 'Failed to load holidays.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const visible = useMemo(
    () => (filter === 'upcoming' ? rows.filter((r) => r.dte >= 0) : rows),
    [filter, rows],
  );

  // First holiday with dte >= 0 is the next one users need to plan around.
  // Used to (a) highlight its row in the table and (b) show an "Upcoming
  // Holiday" chip in the filter bar above it.
  const nextHoliday = useMemo(
    () => rows.find((r) => r.dte >= 0) ?? null,
    [rows],
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <CalendarOff className="w-4 h-4" />
              Trading Holidays ({visible.length})
            </span>
          </CardTitle>
          <button
            onClick={fetchHolidays}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Filter bar — 3-column grid keeps the Upcoming Holiday chip centered
            regardless of the filter buttons' width. Stacks on mobile. */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3">
          {/* Left: filter buttons */}
          <div className="flex flex-wrap items-center gap-2 sm:justify-self-start">
            <button
              onClick={() => setFilter('upcoming')}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                filter === 'upcoming'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
              )}
            >
              Upcoming
            </button>
            <button
              onClick={() => setFilter('all')}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
              )}
            >
              All
            </button>
          </div>

          {/* Center: Upcoming Holiday chip (only when one exists).
              Wraps internally on narrow viewports so the chip never overflows
              the row horizontally. */}
          {nextHoliday ? (
            <span
              className="sm:justify-self-center inline-flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs"
              title={`The next NSE/BSE trading holiday is ${fmtDate(nextHoliday.date)}`}
            >
              <CalendarHeart className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="text-amber-700 dark:text-amber-300 font-medium uppercase tracking-wider">Upcoming Holiday</span>
              <span className="text-gray-700 dark:text-gray-200 font-semibold">{nextHoliday.name}</span>
              <span className="text-gray-500 dark:text-gray-400">·</span>
              <span className="text-gray-700 dark:text-gray-200 tabular-nums">{fmtDate(nextHoliday.date)}</span>
              <span className="text-gray-500 dark:text-gray-400">·</span>
              <span className="text-amber-700 dark:text-amber-300 font-medium tabular-nums">
                {nextHoliday.dte === 0
                  ? 'Today'
                  : `in ${nextHoliday.dte} day${nextHoliday.dte === 1 ? '' : 's'}`}
              </span>
            </span>
          ) : (
            <span />
          )}

          {/* Right: invisible balancer column (keeps the chip optically centered) */}
          <div className="hidden sm:block" />
        </div>

        {/* Body */}
        {error ? (
          <div className="px-6 py-8 flex items-center justify-center gap-2 text-sm text-red-700 dark:text-red-300">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        ) : loading && rows.length === 0 ? (
          <div className="px-6 py-12 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            Loading holidays…
          </div>
        ) : visible.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            {filter === 'upcoming' ? 'No upcoming holidays.' : 'No holidays available.'}
          </div>
        ) : (
          <>
            {/* Mobile (<md): card list. Each holiday is a self-contained card. */}
            <ul className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {visible.map((h) => {
                const isNext = nextHoliday !== null && h.date === nextHoliday.date;
                return (
                  <li
                    key={h.date}
                    className={cn(
                      'px-4 py-3',
                      h.dte < 0 && 'opacity-60',
                      isNext && 'bg-amber-50/60 dark:bg-amber-900/20 border-l-2 border-amber-500',
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className={cn(
                          'text-sm text-gray-900 dark:text-white tabular-nums',
                          isNext ? 'font-semibold' : 'font-medium',
                        )}>
                          {fmtDate(h.date)}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">
                          {h.name}
                        </div>
                        {isNext && (
                          <span
                            className="inline-block mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-600 text-white"
                            title="Upcoming Holiday — next upcoming NSE/BSE trading holiday"
                          >
                            Upcoming Holiday
                          </span>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          DTE
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white tabular-nums mt-0.5">
                          {h.dte < 0
                            ? `${h.dte}`
                            : h.dte === 0
                              ? 'Today'
                              : `${h.dte}d`}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Desktop (md+): table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Holiday</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">DTE</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {visible.map((h) => {
                    const isNext = nextHoliday !== null && h.date === nextHoliday.date;
                    return (
                      <tr
                        key={h.date}
                        className={cn(
                          'hover:bg-gray-50 dark:hover:bg-gray-700',
                          h.dte < 0 && 'opacity-60',
                          isNext && 'bg-amber-50/60 dark:bg-amber-900/20 border-l-2 border-amber-500',
                        )}
                      >
                        <td className={cn(
                          'px-6 py-3 text-sm text-gray-900 dark:text-white tabular-nums whitespace-nowrap',
                          isNext ? 'font-semibold' : 'font-medium',
                        )}>
                          <div className="inline-flex items-center gap-1.5">
                            <span>{fmtDate(h.date)}</span>
                            {isNext && (
                              <span
                                className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-600 text-white"
                                title="Upcoming Holiday — next upcoming NSE/BSE trading holiday"
                              >
                                Upcoming
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {h.name}
                        </td>
                        <td className="px-6 py-3 text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                          {h.dte < 0
                            ? `${h.dte}`
                            : h.dte === 0
                              ? 'Today'
                              : `${h.dte} day${h.dte === 1 ? '' : 's'}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default HolidaysSection;
